'use client';

import { Accordion, AccordionItem } from '@coolblue-development/freddy';
import { Box, Flex, Typography } from '@coolblue-development/freddy';
import { InfoUSP } from '@coolblue-development/next-patterns/client';
import { useRequestContext } from '@coolblue-development/next-request-context/client';
import { SalesPrice, SalesPriceFormer } from '@coolblue-development/next-sales-price/client';
import { SummaryItemType } from '@features/add-to-cart-page/components/SummaryStore/SummaryStore.types';
import type { Accessory } from '@features/add-to-cart-page/typings/Product';
import { USBCChargerIncluded } from '@features/shared/USBCChargerIncluded';
import { useTranslation } from '@i18n/useTranslation';
import { EnabledCrossSellChargerProductTypes } from 'src/constants';

import type { SummaryItem } from '../../SummaryStore';
import { mapAccessoryProductToSummaryItem, useSummaryStore } from '../../SummaryStore';
import CrossSellCharger from '../ProductSummary/CrossSellCharger';
import ProductPrice from '../ProductSummary/ProductPrice';
import { groupItemsByType } from './utils';

type TranslationFunction = Awaited<ReturnType<typeof useTranslation>>['t'];

function renderCrossSellCharger({
  missingCharger,
  crossSellChargers,
  isInMissingChargerWordingSplitTest
}: {
  missingCharger: boolean;
  crossSellChargers: SummaryItem[];
  isInMissingChargerWordingSplitTest: boolean;
}) {
  if (!missingCharger) return null;

  const addedToCart = crossSellChargers.length > 0;
  if (!addedToCart) {
    return <USBCChargerIncluded hasUsbCChargerIncluded={false} />;
  }

  return (
    <CrossSellCharger
      isInMissingChargerWordingSplitTest={isInMissingChargerWordingSplitTest}
      charger={crossSellChargers[0]}
    />
  );
}

function renderFreeWithListItem({
  summaryItem,
  index,
  t,
  isBusiness
}: {
  summaryItem: SummaryItem;
  index: number;
  t: TranslationFunction;
  isBusiness: boolean;
}) {
  if (summaryItem.type !== SummaryItemType.FreeWith) return null;

  return (
    <Flex
      component='li'
      justifyContent='space-between'
      gap={200}
      key={summaryItem.id ?? `${index}-${summaryItem.name}`}
    >
      <Box flexGrow={1}>
        <InfoUSP
          productTitle={summaryItem.name}
          promoValue={
            <SalesPrice
              currentPrice={<></>}
              formerPrice={
                <SalesPriceFormer
                  price={summaryItem.listPrice?.includingVat ?? 0}
                  prefix={t('worth')}
                />
              }
            />
          }
          hideIcon
        />
      </Box>
      <ProductPrice
        itemType='complementary'
        product={summaryItem}
        isBusinessCustomer={isBusiness}
      />
    </Flex>
  );
}

function renderPromotionListItem({
  summaryItem,
  index,
  t,
  isBusiness
}: {
  summaryItem: SummaryItem;
  index: number;
  t: TranslationFunction;
  isBusiness: boolean;
}) {
  if (summaryItem.type !== SummaryItemType.Promotion) return null;

  return (
    <Flex
      component='li'
      justifyContent='space-between'
      gap={200}
      key={summaryItem.id ?? `${index}-${summaryItem.name}`}
    >
      <Box flexGrow={1}>
        <InfoUSP
          productTitle={summaryItem.name}
          promoValue={
            <SalesPrice
              currentPrice={<></>}
              {...(summaryItem.listPrice &&
                summaryItem.salesPrice.includingVat && {
                  formerPrice: (
                    <SalesPriceFormer
                      brandName={summaryItem.brand?.name}
                      price={summaryItem.listPrice.includingVat}
                    />
                  )
                })}
              size='small'
            />
          }
          suffix={t('discount')}
          hideIcon
        />
      </Box>
      <ProductPrice
        itemType='complementary'
        product={{
          ...summaryItem,
          listPrice: { includingVat: summaryItem.listPrice?.includingVat ?? 0 }
        }}
        isBusinessCustomer={isBusiness}
        formerPriceLabel={t('worth')}
      />
    </Flex>
  );
}

function renderGenericListItem({
  summaryItem,
  index,
  isBusiness
}: {
  summaryItem: SummaryItem;
  index: number;
  isBusiness: boolean;
}) {
  return (
    <Flex
      component='li'
      alignItems='flex-start'
      gap={200}
      key={summaryItem.id ?? `${index}-${summaryItem.name}`}
    >
      <Typography
        variant='h5'
        flexGrow={1}
        mb={0}
        fontWeight={400}
        component='span'
        lineHeight={500}
      >
        {summaryItem.quantity > 1 && (
          <Box color='subtle' display='inline' component='span'>
            {summaryItem.quantity}x{' '}
          </Box>
        )}

        {summaryItem.name}
      </Typography>

      {isBusiness ? (
        <Box flexShrink={0}>
          <ProductPrice
            itemType='complementary'
            product={summaryItem}
            isBusinessCustomer={isBusiness}
          />
        </Box>
      ) : (
        summaryItem.showPrice && (
          <ProductPrice
            itemType='complementary'
            product={summaryItem}
            isBusinessCustomer={isBusiness}
          />
        )
      )}
    </Flex>
  );
}

function renderGenericList({
  summaryItems,
  title,
  key,
  t,
  isBusiness,
  isInternalUser
}: {
  summaryItems: SummaryItem[];
  title: string;
  key: string;
  t: TranslationFunction;
  isBusiness: boolean;
  isInternalUser: boolean;
}) {
  const mappedSummaryItems = summaryItems.map((summaryItem, index) => {
    if (summaryItem.type === SummaryItemType.FreeWith) {
      return renderFreeWithListItem({ summaryItem, index, t, isBusiness });
    }

    if (summaryItem.type === SummaryItemType.Promotion) {
      return renderPromotionListItem({ summaryItem, index, t, isBusiness });
    }

    return renderGenericListItem({ summaryItem, index, isBusiness });
  });

  if (summaryItems.length === 0 || mappedSummaryItems.filter(item => item !== null).length === 0) {
    return null;
  }

  if (summaryItems.length === 1) {
    const labelId = `${key}-label`;
    return (
      <div key={key}>
        <Typography variant='h5' lineHeight='2.0rem' component='span' id={labelId}>
          {title}
        </Typography>
        <Flex
          component='ul'
          direction='column'
          gap={200}
          data-cy={isInternalUser ? 'added-accessories-list' : undefined}
          my={0}
          pl={0}
          aria-labelledby={labelId}
        >
          {mappedSummaryItems}
        </Flex>
      </div>
    );
  }

  return (
    <Accordion arrowPosition='start' key={key}>
      <AccordionItem title={title} data-cy={isInternalUser ? 'added-accessories-list' : undefined}>
        <Flex direction='column' gap={200} component='ul' my={0} pl={0}>
          {mappedSummaryItems}
        </Flex>
      </AccordionItem>
    </Accordion>
  );
}

export default function ComplementaryItems({
  missingCharger = false,
  isBusiness = false,
  isInMissingChargerWordingSplitTest = false
}: {
  missingCharger?: boolean;
  isBusiness?: boolean;
  isInMissingChargerWordingSplitTest?: boolean;
}) {
  const { t } = useTranslation('webshop.addToCart');
  const { items } = useSummaryStore();
  const { isInternalUser } = useRequestContext();

  if (items.length === 0 && !missingCharger) return null;

  const groupedItems = groupItemsByType(items);
  const accessories = groupedItems.accessory ?? [];
  const insurances = groupedItems.insurance ?? [];
  const extendedWarranties = groupedItems['extended-warranty'] ?? [];
  const extraServices = groupedItems.service ?? [];
  const promotions = groupedItems.promotion ?? [];
  const freeWiths = groupedItems['free-with'] ?? [];
  const crossSellChargers = groupedItems['cross-sell-charger'] ?? [];

  const accessoryChargerItem = items.find(item =>
    EnabledCrossSellChargerProductTypes.includes((item as unknown as Accessory).productTypeId)
  );
  const accessoryCharger =
    accessoryChargerItem &&
    mapAccessoryProductToSummaryItem(accessoryChargerItem as unknown as Accessory, 1);

  const genericItems = {
    addedAccessories: accessories.filter(item => item.id !== accessoryCharger?.id),
    insurance: insurances,
    extendedWarranty: extendedWarranties,
    extraServices,
    promotions,
    freeWiths
  };

  return (
    <Flex direction='column' gap={200}>
      {renderCrossSellCharger({
        missingCharger,
        crossSellChargers,
        isInMissingChargerWordingSplitTest
      })}
      {Object.keys(genericItems).map(key =>
        renderGenericList({
          summaryItems: genericItems[key as keyof typeof genericItems],
          title: t(key),
          key,
          t,
          isBusiness,
          isInternalUser
        })
      )}
    </Flex>
  );
}
